import clsx from 'clsx'
import React, {
  CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Control, Controller, RegisterOptions } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { IconSvg, Image } from '../../atoms'
import { divideByFactor } from '../../../utils/prices'

export interface CommonInputTextRef extends HTMLDivElement {
  setValue: React.Dispatch<React.SetStateAction<string>>
}

export interface ITextInputProps {
  title?:
    | string
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined
  width?: string | undefined
  rules: Omit<
    RegisterOptions<any, any>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  control: Control<any, any>
  name: string
  style?: CSSProperties
  className?: string
  placeholder?: string
  units?: string
  disable?: boolean
  image?: string
  formatValue?: (arg: string) => string | undefined
  inputType?: React.HTMLInputTypeAttribute | undefined
  checkInput?: (str: string) => boolean
  min?: number | ((value: number, initialValue: number) => number)
  max?: number
  reset?: boolean
  onBlur?: () => void
  transformValue?: (arg: string) => any
}

const checkIsVat = (str: string): boolean => {
  if (str.match(/^\d*[,.]?(\d?){2}$/)) {
    return true
  }
  return false
}

const valueToVat = (value: string) => {
  const valueToArray = value.split('.')
  if (valueToArray.length > 1) {
    if (valueToArray[1].length === 0) {
      return value.concat('00 %')
    }
    if (valueToArray[1].length === 1) {
      return value.concat('0 %')
    }
    if (valueToArray[1].length === 2) {
      return value.concat(' %')
    }
  } else {
    return value.concat(',00 %')
  }
}

const CommonInputText = forwardRef(
  (
    {
      name,
      control,
      rules,
      title,
      placeholder,
      style,
      className,
      units,
      disable,
      image,
      min,
      max,
      formatValue,
      inputType,
      reset,
      onBlur,
      transformValue = (value) => value,
    }: ITextInputProps,
    refParent,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [value, setValue] = useState<string>('')
    const ref = useRef<HTMLInputElement>(null)

    useImperativeHandle(refParent, () => ({
      setValue,
    }))

    return (
      <Controller
        name={name}
        rules={rules}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const handleChange = (e: any) => {
            let value: string = e.target.value

            value = value.replace(',', '.')
            if (parseFloat(value) > 100) {
              value = '100'
            }

            // field.onChange(Number(value) * 100)
            setValue(value)
          }

          useEffect(() => {
            if (field.value !== undefined) {
              setValue(divideByFactor(field.value).toString())
            }
          }, [])

          return (
            <div
              className={clsx(s.container, className, {
                [s.containerReset]: reset,
              })}
              style={style}
            >
              {title && <h4 className={s.title}>{title}</h4>}
              <div className={clsx(s.customInput, { [s.disabled]: disable })}>
                <div className={s.inputText}>
                  {image && (
                    <div className={s.containerImage}>
                      <Image src={image} type='cover' alt='' />
                    </div>
                  )}
                  <input
                    value={value}
                    onClick={() => setIsFocused(true)}
                    onChange={handleChange}
                    placeholder={placeholder}
                    type={inputType}
                    size={1}
                    disabled={disable}
                    ref={ref}
                    onBlur={() => {
                      setIsFocused(false)
                      let inputValue = value

                      if (value && value !== '') {
                        if (typeof min === 'function') {
                          // value = min(parseFloat(value), parseFloat(initialValue)).toString()
                        } else if (
                          typeof min === 'number' &&
                          parseFloat(value) < min
                        ) {
                          inputValue = min.toString()
                        }

                        if (max !== undefined && parseFloat(value) > max) {
                          inputValue = max.toString()
                        }

                        field.onChange(transformValue(inputValue))
                        setValue(inputValue)
                      }

                      field.onBlur()
                      if (onBlur) {
                        onBlur()
                      }
                    }}
                  />
                </div>
                {formatValue &&
                  field.value !== '' &&
                  field.value !== undefined &&
                  !isFocused && (
                    <div
                      className={clsx(s.formattedValue, s.customInput, {
                        [s.disabled]: disable,
                      })}
                      onClick={() => {
                        if (ref.current) {
                          ref.current.focus()
                        }
                        setIsFocused(true)
                      }}
                    >
                      {formatValue(value)}
                    </div>
                  )}
                {units && (
                  <div className={s.units}>
                    <span>{units}</span>
                  </div>
                )}
              </div>
              {reset && (
                <div
                  className={clsx(s.reset, { [s.disabled]: disable })}
                  onClick={() => !disable && field.onChange('')}
                >
                  <IconSvg.Reset />
                  <span>
                    <FormattedMessage
                      defaultMessage='Réinitialiser'
                      id='ui.CommonInputText.reset'
                    />
                  </span>
                </div>
              )}
              {error && error.message && (
                <span className={s.form__error}>{error.message}</span>
              )}
            </div>
          )
        }}
      />
    )
  },
)

const InputTypeTextPercentage = forwardRef(
  ({ inputType, placeholder, ...props }: ITextInputProps, refParent) => {
    return (
      <CommonInputText
        {...props}
        formatValue={(value) => valueToVat(value.toString())}
        checkInput={checkIsVat}
        placeholder={placeholder ?? '0,00 %'}
        inputType={inputType}
        ref={refParent}
      />
    )
  },
)

export default InputTypeTextPercentage
